export class ArchiveAudioRecorder {
  private mediaRecorder: MediaRecorder | null = null
  private chunks: Blob[] = []
  private stream: MediaStream | null = null
  /** How many chunks {@link takeUnsentChunks} has already handed out. */
  private sentChunkCount = 0

  start(stream: MediaStream): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      return
    }

    this.stream = stream
    this.chunks = []
    this.sentChunkCount = 0

    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : ''

    this.mediaRecorder = mimeType
      ? new MediaRecorder(stream, { mimeType })
      : new MediaRecorder(stream)

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.chunks.push(event.data)
    }

    this.mediaRecorder.start(1000)
  }

  isRecording(): boolean {
    return this.mediaRecorder !== null && this.mediaRecorder.state !== 'inactive'
  }

  /**
   * Everything recorded since the previous call, as one blob to upload while the
   * meeting is still running (BRAI-457).
   *
   * The chunks are **not** removed: `stop()` must still return the whole
   * recording, because a meeting that ends normally uploads that file and the
   * parts are thrown away. What this tracks is only how far the periodic upload
   * has got, so the parts tile the recording end to end with no overlap — which
   * is what lets the server stitch them back into the same bytes.
   *
   * The first call therefore includes chunk 0, the only one carrying the WebM
   * header; a recording stitched from parts starting anywhere else is unplayable.
   *
   * @returns The new chunks as one `audio/webm` blob, or null when nothing has
   *   been recorded since the previous call.
   */
  takeUnsentChunks(): Blob | null {
    if (this.sentChunkCount >= this.chunks.length) return null
    const unsent = this.chunks.slice(this.sentChunkCount)
    this.sentChunkCount = this.chunks.length
    return new Blob(unsent, { type: this.mediaRecorder?.mimeType || 'audio/webm' })
  }

  /**
   * Suspends recording without ending the session, so the meeting's audio stays
   * one continuous WebM across a network outage (BRAI-366).
   *
   * `MediaRecorder.pause()` leaves the state at `'paused'` rather than
   * `'inactive'`, which is what keeps `chunks` — and therefore everything
   * recorded before the outage — alive: `start()` clears them, and its
   * `isRecording()` guard only lets that happen once a session has truly ended.
   *
   * A no-op when the recorder is not currently recording, or on a browser
   * without `pause` — there the recording simply keeps running and captures the
   * outage as audio, which is the old behaviour rather than a new failure.
   */
  pause(): void {
    const recorder = this.mediaRecorder
    if (!recorder || recorder.state !== 'recording') return
    if (typeof recorder.pause !== 'function') return
    recorder.pause()
  }

  /** Resumes a {@link pause}d session, continuing the same recording. */
  resume(): void {
    const recorder = this.mediaRecorder
    if (!recorder || recorder.state !== 'paused') return
    if (typeof recorder.resume !== 'function') return
    recorder.resume()
  }

  async stop(): Promise<Blob | null> {
    const recorder = this.mediaRecorder
    if (!recorder || recorder.state === 'inactive') {
      const blob = this.chunks.length > 0 ? new Blob(this.chunks, { type: 'audio/webm' }) : null
      this.cleanup()
      return blob
    }

    return new Promise((resolve) => {
      recorder.onstop = () => {
        const blob =
          this.chunks.length > 0
            ? new Blob(this.chunks, { type: recorder.mimeType || 'audio/webm' })
            : null
        this.cleanup()
        resolve(blob)
      }
      recorder.stop()
    })
  }

  private cleanup(): void {
    this.mediaRecorder = null
    this.stream?.getTracks().forEach((track) => track.stop())
    this.stream = null
    this.chunks = []
    this.sentChunkCount = 0
  }
}
